import { Suspense, createElement, lazy, type ComponentType, type ReactElement } from 'react'
import type { RouteObject } from 'react-router-dom'
import { MANIFEST, type FlatRoute, type NavSection } from '../nav/manifest.ts'
import { EmptyState } from '../components/ui/EmptyState'
import { PendingScreen } from './PendingScreen.tsx'
import { NotFoundScreen } from './NotFoundScreen.tsx'
import { buildRouteTable, type RouteTableEntry } from './routeTable.ts'

/**
 * The element adapter: the one place a route entry becomes something a router can render.
 *
 * It is a .ts and not a .tsx on purpose. There is no JSX below, only `createElement`, because
 * the component and its props are decided by the table: spelling each one out as a tag would
 * put route knowledge back into this file, which is the thing the manifest exists to prevent.
 *
 * `routeTable.ts` decides the shape of the console; this turns each entry into the object
 * the router consumes. The router's `RouteObject` is imported as a TYPE only, so nothing of
 * react-router reaches the runtime here and `routeTable.ts` stays testable under plain
 * `node`. Hand-writing an equivalent type was the alternative and it was worse: it is a copy
 * of theirs that stops matching the day they change it, and the mismatch then shows up as a
 * route that does not render rather than as a type error.
 */
export type RouterRoute = RouteObject & { id: string }

/**
 * The loading state while a screen's chunk arrives.
 *
 * NOT `null`, and not `PendingScreen`. `null` is a blank frame, which is the failure this
 * console keeps writing rules against — indistinguishable from a screen that rendered nothing.
 * `PendingScreen` is worse: it is the `dayN` EmptyState, so a slow chunk would tell an operator
 * the screen had not been built yet.
 *
 * In practice this is invisible: the chunk is fetched from the same origin and the screens are
 * small. It matters on a cold load over a bad connection, which is exactly when a blank frame
 * would be read as a broken console.
 */
function loadingFor(route: FlatRoute | null): ReactElement {
  return createElement(EmptyState, {
    icon: route?.icon ?? 'loader',
    eyebrow: 'LOADING',
    title: route ? `Loading ${route.title}.` : 'Loading.',
  })
}

/**
 * `React.lazy` is created ONCE per entry, here, and never inside a render.
 *
 * `buildRoutes()` is called once at module scope by `App.tsx`, so this runs once per route. A
 * `lazy()` created during a render would be a new component type on every render, and React
 * would unmount and remount the screen — losing its state each time, which on the client detail
 * screens would mean losing a form mid-typing.
 */
function elementFor(entry: RouteTableEntry): ReactElement {
  // `createElement` rather than JSX: the component and its props are decided by the table,
  // and spelling each one out as a tag here would put route knowledge back in this file.
  if (entry.load) {
    const Screen = lazy(entry.load) as ComponentType<{ route: FlatRoute }>
    return createElement(
      Suspense,
      { fallback: loadingFor(entry.route) },
      entry.route ? createElement(Screen, { route: entry.route }) : createElement(Screen as ComponentType),
    )
  }

  if (!entry.Component) {
    // Unreachable by construction: `routeTable.ts` sets exactly one of `load` and `Component`.
    // Stated rather than asserted away, because the cast that used to stand here is what let
    // `Component` become optional without anything noticing.
    throw new Error(`route "${entry.id}" has neither a component nor a screen to load`)
  }

  return entry.route
    ? createElement(entry.Component as ComponentType<{ route: FlatRoute }>, { route: entry.route })
    : createElement(entry.Component as ComponentType)
}

function toRouterRoute(entry: RouteTableEntry): RouterRoute {
  // The router's own type is a union: an index route may not carry a path or children, and a
  // non-index route may not carry `index: true`. Building the two branches separately keeps
  // that true, rather than asserting it away with a cast.
  if (entry.index) {
    return { index: true, id: entry.id, element: elementFor(entry) }
  }

  return {
    path: entry.path,
    id: entry.id,
    element: elementFor(entry),
    ...(entry.children?.length ? { children: entry.children.map(toRouterRoute) } : {}),
  }
}

/** The complete route table, ready for the router. */
export function buildRoutes(manifest: NavSection[] = MANIFEST): RouterRoute[] {
  return buildRouteTable({ pending: PendingScreen, notFound: NotFoundScreen }, manifest).map(toRouterRoute)
}

export { buildRouteTable, flattenTable, type RouteTableEntry } from './routeTable.ts'

// The two shell screens are deliberately NOT re-exported here. This file is the route
// table; a barrel that also re-exports components makes every consumer of buildRoutes pull
// them in, and it is what react-refresh warns about. Import them from their own modules.
